import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import type {DetalhesComprasGov} from '../../../types/priceResearch'
import {montarSecoes} from './comprasGovDetalhes'

// Azul-marinho e cinzas do modal desenhado em captures/modal-a.png (spec LCTO-2032).
export const COR_DETALHES = '#012441'
const COR_MUDA = '#7a8896'
const COR_LINHA = '#f1f5f9'

const MONO = "ui-monospace,'SF Mono',Menlo,monospace"

type Props = {
    open: boolean
    detalhes: DetalhesComprasGov
    link: string // JSON bruto do portal de Dados Abertos ("Ver dados brutos")
    onClose: () => void
}

// Detalhes da compra do Compras.gov que sustenta um preço da revisão: lista em seções, o
// JSON bruto a um clique (nova aba, o modal continua aberto para comparar os dois).
export default function ComprasGovDetalhesModal({open, detalhes, link, onClose}: Props) {
    const secoes = montarSecoes(detalhes)
    return (
        <Dialog
            open={open}
            onClose={() => onClose()}
            fullWidth
            maxWidth={false}
            slotProps={{paper: {sx: {maxWidth: 600, borderRadius: '8px'}}}}
        >
            <DialogTitle
                sx={{
                    p: '16px 24px 4px',
                    fontSize: 18,
                    fontWeight: 600,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 1.5,
                }}
            >
                Detalhes do preço — Compras.gov.br
                <IconButton aria-label="Fechar janela" onClick={() => onClose()} size="small" sx={{color: '#94a3b8', mt: -0.5, mr: -1}}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <Typography sx={{px: 3, fontSize: 12, color: COR_MUDA}}>
                Compra sem publicação no PNCP (ex.: anterior à Lei 14.133). Dados do portal de Dados Abertos.
            </Typography>
            <DialogContent sx={{p: '12px 24px 8px'}}>
                {secoes.map((s) => (
                    <Box key={s.titulo} component="section">
                        <Typography
                            component="h3"
                            sx={{
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: '.04em',
                                textTransform: 'uppercase',
                                color: COR_MUDA,
                                m: '14px 0 6px',
                            }}
                        >
                            {s.titulo}
                        </Typography>
                        {s.linhas.map((l) => (
                            <Box
                                key={l.rotulo}
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '150px 1fr',
                                    gap: 1,
                                    py: '5px',
                                    fontSize: 13,
                                    borderBottom: `1px solid ${COR_LINHA}`,
                                }}
                            >
                                <Box component="span" sx={{fontWeight: 500, color: COR_MUDA, fontSize: 12}}>
                                    {l.rotulo}
                                </Box>
                                <Box
                                    component="span"
                                    sx={l.mono ? {fontFamily: `${MONO} !important`, fontSize: 12} : {overflowWrap: 'anywhere'}}
                                >
                                    {l.destaque ? (
                                        <>
                                            <strong>{l.destaque}</strong>
                                            {l.resto}
                                        </>
                                    ) : (
                                        l.valor
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                ))}
            </DialogContent>
            <DialogActions
                sx={{justifyContent: 'space-between', p: '10px 24px 16px', borderTop: `1px solid ${COR_LINHA}`, mt: 1}}
            >
                <Button
                    variant="outlined"
                    onClick={() => window.open(link, '_blank', 'noopener')}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        color: COR_DETALHES,
                        borderColor: '#cbd5e1',
                        '&:hover': {borderColor: COR_DETALHES, backgroundColor: 'transparent'},
                    }}
                >
                    Ver dados brutos ↗
                </Button>
                <Button
                    variant="contained"
                    onClick={() => onClose()}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        backgroundColor: COR_DETALHES,
                        '&:hover': {backgroundColor: COR_DETALHES},
                    }}
                >
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    )
}
